"use client";
import React, { useState, useEffect } from "react";
import {
  Card,
  Typography,
  Box,
  Tabs,
  Tab,
  Button,
  Stack,
  Snackbar,
  Fade,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import UserInfo from "./UserInfo";
import { useNavigate, useParams } from "react-router-dom";
import config from "../../../../config";

function CustomTabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ p: 3 }}>{children}</Box> : null;
}

const AddUser = () => {
  const [value, setValue] = useState(0);
  const navigate = useNavigate();
  const { admin_user_id } = useParams();

  // ✅ Treat edit mode as true ONLY if param is a proper UUID
  const isValidUuid =
    !!admin_user_id &&
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
      admin_user_id
    );
  const isEdit = isValidUuid;

  // Form states
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("user");
  const [profilePicture, setProfilePicture] = useState(null);

  // Original values for cancel functionality
  const [originalValues, setOriginalValues] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    password: "",
    role: "user",
    profilePicture: null
  });

  // UI states
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [isFormValid, setIsFormValid] = useState(false);

  const handleChange = (event, newValue) => setValue(newValue);

  // Prefill when editing (only if a valid UUID is present)
  useEffect(() => {
    if (!isEdit) return;

    const fetchUserDetails = async () => {
      try {
        const res = await fetch(`${config.API_BASE_URL_V1}/admin/user/details/${admin_user_id}`, {
          headers: config.getHeaders()
        });
        const json = await res.json();
        if (res.ok && json?.data) {
          const u = json.data;
          const firstName_val = u.first_name || "";
          const lastName_val = u.last_name || "";
          const email_val = u.email_id || "";
          const phoneNumber_val = u.phone_number || "";
          const password_val = "";
          const role_val = u.role || "user";

          let picture_val = null;
          if (u.profile_picture_url) {
            const pictureUrl = u.profile_picture_url.startsWith('/')
              ? `${config.API_BASE_URL_V1}${u.profile_picture_url}`
              : u.profile_picture_url;
            picture_val = pictureUrl;
          }

          // Set form values
          setFirstName(firstName_val);
          setLastName(lastName_val);
          setEmail(email_val);
          setPhoneNumber(phoneNumber_val);
          setPassword(password_val);
          setRole(role_val);
          setProfilePicture(picture_val);

          // Store original values for cancel functionality
          setOriginalValues({
            firstName: firstName_val,
            lastName: lastName_val,
            email: email_val,
            phoneNumber: phoneNumber_val,
            password: password_val,
            role: role_val,
            profilePicture: picture_val
          });
        } else {
          setSnackbarMsg(json?.message || "Failed to load user details");
          setSnackbarOpen(true);
        }
      } catch (err) {
        setSnackbarMsg("Error fetching user details");
        setSnackbarOpen(true);
      }
    };

    fetchUserDetails();
  }, [isEdit, admin_user_id]);

  const handleCancel = () => {
    // Reset all form fields to original values
    setFirstName(originalValues.firstName);
    setLastName(originalValues.lastName);
    setEmail(originalValues.email);
    setPhoneNumber(originalValues.phoneNumber);
    setPassword(originalValues.password);
    setRole(originalValues.role);
    setProfilePicture(originalValues.profilePicture);
  };

  const handleSave = async () => {
    // ✅ Comprehensive validation before submission
    if (!firstName.trim()) {
      setSnackbarMsg("First Name is required.");
      setSnackbarOpen(true);
      return;
    }
    if (firstName.trim().length < 2 || firstName.trim().length > 50) {
      setSnackbarMsg("First Name must be between 2 and 50 characters.");
      setSnackbarOpen(true);
      return;
    }
    if (!/^[a-zA-Z\s'-]+$/.test(firstName.trim())) {
      setSnackbarMsg("First Name can only contain letters, spaces, hyphens, and apostrophes.");
      setSnackbarOpen(true);
      return;
    }

    if (!lastName.trim()) {
      setSnackbarMsg("Last Name is required.");
      setSnackbarOpen(true);
      return;
    }
    if (lastName.trim().length < 2 || lastName.trim().length > 50) {
      setSnackbarMsg("Last Name must be between 2 and 50 characters.");
      setSnackbarOpen(true);
      return;
    }
    if (!/^[a-zA-Z\s'-]+$/.test(lastName.trim())) {
      setSnackbarMsg("Last Name can only contain letters, spaces, hyphens, and apostrophes.");
      setSnackbarOpen(true);
      return;
    }

    if (!email.trim()) {
      setSnackbarMsg("Email is required.");
      setSnackbarOpen(true);
      return;
    }
    if (email.trim().length > 100) {
      setSnackbarMsg("Email must be less than 100 characters.");
      setSnackbarOpen(true);
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setSnackbarMsg("Please enter a valid email address.");
      setSnackbarOpen(true);
      return;
    }

    if (!phoneNumber.trim()) {
      setSnackbarMsg("Phone Number is required.");
      setSnackbarOpen(true);
      return;
    }
    if (!/^[\d\s\-\+\(\)]+$/.test(phoneNumber.trim())) {
      setSnackbarMsg("Phone number can only contain digits, spaces, hyphens, and parentheses.");
      setSnackbarOpen(true);
      return;
    }
    if (phoneNumber.trim().length < 10) {
      setSnackbarMsg("Phone number must be at least 10 characters.");
      setSnackbarOpen(true);
      return;
    }
    if (phoneNumber.trim().length > 20) {
      setSnackbarMsg("Phone number must be less than 20 characters.");
      setSnackbarOpen(true);
      return;
    }

    if (!isEdit && !password.trim()) {
      setSnackbarMsg("Password is required.");
      setSnackbarOpen(true);
      return;
    }
    if (password && password.length < 6) {
      setSnackbarMsg("Password must be at least 6 characters long.");
      setSnackbarOpen(true);
      return;
    }
    if (password && password.length > 50) {
      setSnackbarMsg("Password must be less than 50 characters.");
      setSnackbarOpen(true);
      return;
    }

    // Note: Role is not required - it's set by backend (default: "user")

    const formData = new FormData();
    formData.append("first_name", firstName.trim());
    formData.append("last_name", lastName.trim());
    formData.append("email_id", email.trim());
    formData.append("phone_number", phoneNumber.trim());
    if (password) formData.append("password", password);

    // Only append a file if the user selected a new one (strings are existing URLs)
    if (profilePicture && typeof profilePicture !== "string") {
      formData.append("profile_picture", profilePicture);
    }
    if (isEdit) {
      formData.append("admin_user_id", admin_user_id);
    }

    try {
      setLoading(true);
      const endpoint = isEdit
        ? `${config.API_BASE_URL_V1}/admin/user/update/${admin_user_id}`
        : `${config.API_BASE_URL_V1}/admin/user/create`;

      const res = await fetch(endpoint, {
        method: isEdit ? "POST" : "POST",
        headers: config.getHeaders(),
        body: formData,
      });

      const json = await res.json();

      if (!res.ok || json?.success === false) {
        let errMsg = json?.message || (isEdit ? "Failed to update user" : "Failed to create user");

        // Check if error is due to duplicate email
        if (json?.error?.details && json.error.details.includes("email_id_unique")) {
          errMsg = `${errMsg} - Email already exists`;
        }

        setSnackbarMsg(errMsg);
        setSnackbarOpen(true);
        return;
      }

      setSnackbarMsg(
        json?.message || (isEdit ? "User updated successfully." : "User created successfully.")
      );
      setSnackbarOpen(true);

      setTimeout(() => {
        navigate("/user-management");
      }, 1500);
    } catch (error) {
      setSnackbarMsg(error?.message || "Network error saving user");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const tabLabels = ["User Info"];

  return (
    <Card
      sx={{
        boxShadow: "none",
        borderRadius: "7px",
        mb: "25px",
        padding: { xs: "18px", sm: "20px", lg: "25px" },
      }}
      className="rmui-card"
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "25px" }}>
        <Typography
          variant="h3"
          sx={{ fontSize: { xs: "16px", md: "18px" }, fontWeight: 700 }}
        >
          {isEdit ? "Edit User" : "Add User"}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/user-management")}
        >
          Back
        </Button>
      </Box>

      <Tabs value={value} onChange={handleChange}>
        {tabLabels.map((label, index) => (
          <Tab key={index} label={label} />
        ))}
      </Tabs>

      <CustomTabPanel value={value} index={0}>
        <UserInfo
          firstName={firstName}
          setFirstName={setFirstName}
          lastName={lastName}
          setLastName={setLastName}
          email={email}
          setEmail={setEmail}
          phoneNumber={phoneNumber}
          setPhoneNumber={setPhoneNumber}
          password={password}
          setPassword={setPassword}
          role={role}
          setRole={setRole}
          profilePicture={profilePicture}
          setProfilePicture={setProfilePicture}
          isEdit={isEdit}
          onValidationChange={setIsFormValid}
        />
      </CustomTabPanel>

      <Stack direction="row" alignItems="center" justifyContent="center" gap={2} mt={2}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={loading || !isFormValid}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          sx={{ minWidth: 120 }}
        >
          {loading ? "Submitting..." : isEdit ? "Update" : "Submit"}
        </Button>
        {isEdit && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleCancel}
            disabled={loading}
            sx={{ minWidth: 120 }}
          >
            Cancel
          </Button>
        )}
      </Stack>

      <Snackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMsg}
        autoHideDuration={3000}
        TransitionComponent={Fade}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Card>
  );
};

export default AddUser;
